import Modal from "components/modal/Modal";
import x_mark from "assets/images/x.svg";
import { EXIT_SURVEY_QUESTIONS } from "utilities/Constant";
import { useMemo, useState } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { selectPreferredLanguage } from "features/authSlice";
import { getPreferredLanguage, isSpanishLanguage } from "utilities/getPreferredLanguage";
import "./exit-survey-modal.scss";
import { Button } from "components/v2/button/Button";
import { Feedback } from "../surveyService";
import { LoadingOverlay } from "components/ui/loading-spinners";

interface ExitSurveyModalProps {
  readonly closeSurvey: (partialFeedback: Feedback[]) => void;
  readonly submitSurvey: (feedback: Feedback[]) => void;
  readonly isLoading?: boolean;
}

export default function ExitSurveyModal({ closeSurvey, submitSurvey, isLoading }: ExitSurveyModalProps) {
  const preferredLanguage = useSelector(selectPreferredLanguage);
  const isSpanishUi = useMemo(
    () => isSpanishLanguage(getPreferredLanguage(preferredLanguage)),
    [preferredLanguage]
  );
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [comment, setComment] = useState("");

  const handleOptionChange = (questionIndex: number, option: string) => {
    if (questionIndex == 0) setComment("");
    setAnswers((prev) => ({ ...prev, [questionIndex]: option }));
  };

  const submitFeedback = () => {
    const feedback: Feedback[] = [];
    EXIT_SURVEY_QUESTIONS.forEach((item, index) => {
      feedback.push({
        question: item.question,
        answer: answers[index] === "Other" ? comment : answers[index],
      });
    });
    submitSurvey(feedback);
  };

  const handleCloseSurvey = () => {
    const partialFeedback: Feedback[] = EXIT_SURVEY_QUESTIONS.reduce<Feedback[]>((acc, item, index) => {
      if (answers[index] !== undefined) {
        acc.push({
          question: item.question,
          answer: answers[index] === "Other" ? comment : answers[index],
        });
      }
      return acc;
    }, []);
    closeSurvey(partialFeedback);
  };

  return (
    <div className={classNames("exit-modal", { "exit-modal--spanish": isSpanishUi })}>
      <Modal
        customHeader={
          <div className="exit-modal__header">
            {!isLoading && (
              <>
                <div>
                  <h6 className="exit-modal__heading">Before You Go</h6>
                </div>
                <button
                  type="button"
                  className="exit-modal__close-icon"
                  onClick={handleCloseSurvey}
                  onKeyDown={(e) => e.key === "Enter" && handleCloseSurvey()}
                  aria-label="Close survey"
                >
                  <img src={x_mark} alt="Close icon" />
                </button>
              </>
            )}
          </div>
        }
        customBody={
          <div className="exit-modal__body">
            {isLoading ? (
              <LoadingOverlay displayFullScreen={false} />
            ) : (
              <>
                <div className="exit-modal__subheading">
                  <p className="exit-modal__subheading-text">
                    Your feedback helps us improve our products. For additional questions or concerns, please call our support team at 877-214-4431.
                  </p>
                </div>
                <div className="exit-modal__questions">
                  {EXIT_SURVEY_QUESTIONS.map((item, questionIndex) => (
                    <div key={item.question} className="exit-modal__question-block">
                      <p className="exit-modal__question">{item.question}</p>
                      <div className="exit-modal__options">
                        {item.options.map((option) => (
                          <label
                            key={option}
                            className="exit-modal__option"
                          >
                            <input
                              type="radio"
                              name={`question-${questionIndex}`}
                              value={option}
                              checked={answers[questionIndex] === option}
                              onChange={() => handleOptionChange(questionIndex, option)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleOptionChange(questionIndex, option);
                                }
                              }}
                              className="exit-modal__radio"
                            />
                            <span className="exit-modal__option-text">{option}</span>
                          </label>
                        ))}
                        {answers[questionIndex] === "Other" && (
                          <div className="exit-modal__comment-section">
                            <h5>Please explain (optional)</h5>
                            <textarea placeholder="Enter comment" maxLength={150} onChange={(e) => setComment(e.target.value)} />
                            <span>{comment.length}/150 characters</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="exit-modal__footer">
                  <Button label="Submit Feedback" disabled={Object.keys(answers).length != EXIT_SURVEY_QUESTIONS.length} onClick={submitFeedback} />
                </div>
              </>
            )}
          </div>
        }
      />
    </div>
  );
}
